import { useState, lazy, Suspense } from 'react'
import { Box, BoxProps, Image, Tag, Spinner } from '@chakra-ui/react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import { observer } from 'mobx-react'
import VideoThumbnail from 'react-video-thumbnail'
import { S3FileTypeEnum } from 'constants/aws'
import { EMediaThumbnail, MediaType } from 'constants/media'
import { IMedia } from 'interfaces/media'
import { getS3FileUrl } from 'utils/images'
import MediaThumbnailWithIcon from './components/MediaThumbnailWithIcon'
import { getThumbnailType } from './utils'
import styles from './mediaThumbnail.module.scss'

// Lazy load PDF components
const PDFViewer = lazy(() => import('./components/PDFViewer'))

interface IMediaThumbnailProps extends BoxProps {
  media: IMedia
  pdfThumbnailClassName?: string
}

const MediaThumbnail = (props: IMediaThumbnailProps) => {
  const { media, onClick, pdfThumbnailClassName, ...rest } = props
  const { organizationStore } = useStores()
  const { organization } = organizationStore
  const [videoThumbnailUrl, setVideoThumbnailUrl] = useState<string>(media?.videoThumbnailUrl)

  function handleVideoThumbnail(thumbnail: string): void {
    setVideoThumbnailUrl(thumbnail)
  }

  return (
    <>
      {media?.mediaType === MediaType.IMAGE && (
        <Image
          objectFit="contain"
          alt={media.name}
          src={getS3FileUrl(
            S3FileTypeEnum.IMAGE,
            organizationStore.organization?.id ?? 0,
            media?.image ?? media?.originalImage
          )}
          onClick={onClick}
          {...rest}
        />
      )}
      {media?.mediaType === MediaType.VIDEO && (
        <Box className={styles.videoThumbnail}>
          <VideoThumbnail
            videoUrl={
              media?.url ? media.url : getS3FileUrl(S3FileTypeEnum.VIDEO, organization?.id ?? 0, media?.video ?? '')
            }
            thumbnailHandler={handleVideoThumbnail}
          />
          <Image
            objectFit="contain"
            alt={media?.name}
            src={videoThumbnailUrl ?? media?.videoThumbnailUrl}
            onClick={onClick}
            {...rest}
          />
        </Box>
      )}
      {media?.mediaType === MediaType.EMBED_LINK && (
        <MediaThumbnailWithIcon type={EMediaThumbnail.EMBED} onClick={onClick} {...rest} />
      )}
      {[MediaType.FILE, MediaType.OTHER]?.includes(media?.mediaType ?? MediaType.OTHER) && (
        <>
          {getThumbnailType(media) === EMediaThumbnail.PDF ? (
            <Suspense fallback={<Box className={cx(styles.pdfThumbnail, pdfThumbnailClassName)} display="flex" alignItems="center" justifyContent="center"><Spinner /></Box>}>
              <PDFViewer 
                fileUrl={getS3FileUrl(S3FileTypeEnum.OTHER, organization?.id ?? 0, media?.document)}
                className={cx(styles.pdfThumbnail, pdfThumbnailClassName)}
                onClick={onClick}
                {...rest}
              />
            </Suspense>
          ) : (
            <MediaThumbnailWithIcon type={getThumbnailType(media)} onClick={onClick} {...rest} />
          )}
        </>
      )}
      <Tag size="sm" color="gray.500" border="1px solid #E2E8F0" position="absolute" bottom={2} right={2} zIndex={2}>
        {getThumbnailType(media)}
      </Tag>
    </>
  )
}

export default observer(MediaThumbnail)
